/**
 * Parses newsletter frequency strings into type and details components
 * for styled rendering in the UI.
 *
 * @param frequency - The raw frequency string from the database
 * @returns Object with type (main frequency) and optional details
 *
 * @example
 * parseFrequency("Weekly - Friday") // { type: "Weekly", details: "Friday" }
 * parseFrequency("Daily") // { type: "Daily", details: null }
 * parseFrequency("Weekdays + Saturday") // { type: "Daily", details: "Weekdays + Saturday" }
 */
export function parseFrequency(frequency: string): {
  type: string
  details: string | null
} {
  if (!frequency) {
    return { type: "", details: null }
  }

  const trimmed = frequency.trim()

  // Pattern: "Weekly - [Day]", "Fortnightly - Thursday", etc.
  const weeklyPattern = /^(Weekly|Fortnightly|Biweekly)\s*-\s*(.+)$/i
  const weeklyMatch = trimmed.match(weeklyPattern)
  if (weeklyMatch) {
    return {
      type: weeklyMatch[1],
      details: weeklyMatch[2],
    }
  }

  // Pattern: "Weekdays + Saturday" - treat as Daily with details
  const weekdaysSaturdayPattern = /^(Weekdays)\s*\+\s*(Saturday)$/i
  const weekdaysSaturdayMatch = trimmed.match(weekdaysSaturdayPattern)
  if (weekdaysSaturdayMatch) {
    return {
      type: "Daily",
      details: `${weekdaysSaturdayMatch[1]} + ${weekdaysSaturdayMatch[2]}`,
    }
  }

  // Pattern: "Tuesday + Friday" - could be biweekly, but show as-is for now
  const multiDayPattern = /^(\w+day)\s*\+\s*(\w+day)$/i
  const multiDayMatch = trimmed.match(multiDayPattern)
  if (multiDayMatch) {
    // Normalize spacing: "Day1 + Day2"
    return {
      type: `${multiDayMatch[1]} + ${multiDayMatch[2]}`,
      details: null,
    }
  }

  // Simple standalone frequencies: "Daily", "Weekly", "Monthly", etc.
  // Just return as type with no details
  return {
    type: trimmed,
    details: null,
  }
}
